import React                                         from 'react';
import PropTypes                                     from 'prop-types';
import Flux                                          from '../../flux/Flux';
import ReloadHelper                                  from '../../utils/ReloadHelper';
import Translations                                  from '../../utils/Translations';
import {VXPay}                                       from '../../utils/VXPay';
import {
	formatEffectAsPlainText, formatPrice,
}                                                    from '../../components/Dailybonus/dailyBonusHelpers';
import VXButton                                      from "../../components/SimpleElements/VXButton/VXButton";
import {VXButtonLineConfig}                          from "../../components/SimpleElements/VXButton/VXButtonConfig";
import {COLOR_TYPE_TRANSPARENT, COLOR_TYPE_DISABLED} from "../../components/SimpleElements/VXButton/ButtonHelper";
import STYLES                                        from "../../components/SimpleElements/VXButton/EnumVXButton";
import VXLink                                        from "../../components/SimpleElements/VXLink/VXLink";
import {getCurrencySymbol}                           from '../../utils/CommonUtils.js';

/**
 * @param num
 * @returns {string}
 */
const formatter = (num) => ('0' + num).slice(-2);

/**
 * Format date as dd.MM.YYYY
 * @param {Date} date
 * @returns {string}
 */
function formatDate(date) {
	return formatter(date.getDate()) + '.' + formatter(date.getMonth() + 1) + '.' + date.getFullYear();
}

/**
 * @param {{value: number, currency: string}} price
 * @param {{name: string, value: string, unit: string}} effect
 * @returns {{value: string, currency: string}}
 */
function calculatePriceWithDiscount(price, effect) {
	if (!price) {
		return {
			value:    '0',
			currency: 'EUR',
		};
	}

	const result = {
		value:    '',
		currency: price.currency,
	};

	if (!effect) {
		return result;
	}

	switch (effect.unit) {
		case 'currency':
			result.value = price.value - effect.value;
			break;
		case 'items':
			result.value = price.value;
			break;
		case 'percent':
		default:
			result.value = Number(price.value * (1 - effect.value / 100)).toFixed(2);
			break;
	}

	return result;
}

class CollectedOffersList extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			offers:     null,
			totalCount: 0,
		};

		this.onConsumedOffersReceived = this.onConsumedOffersReceived.bind(this);
		this.onConsumedOffersReloaded = this.onConsumedOffersReloaded.bind(this);
		this.onLoadMore               = this.onLoadMore.bind(this);
		this.onReloadOffers           = this.onReloadOffers.bind(this);
		this.onUrlClickHandler        = this.onUrlClickHandler.bind(this);
		this.onBecomeVIPButtonClicked = this.onBecomeVIPButtonClicked.bind(this);
	}

	componentDidMount() {
		if (Flux.Guest.isLoggedIn()) {
			// the listener onLoadMore() was triggered before the first consumed offer were set in the state.
			// Because of this the Offset in the onLoadMore() function was not calculated correctly and it loaded the same consumed offers again
			Flux.Vxql.getConsumedOffers((result) => {
				const stateCallback = () => {
					Flux.DailyBonus.addLoadedListener(this.onReloadOffers);
				};
				this.onConsumedOffersReloaded(result, stateCallback);
			}, 31, 0, this.props.dateFrom, this.props.dateTo);
		}
	}

	componentWillUnmount() {
		Flux.DailyBonus.removeLoadedListener(this.onReloadOffers);
	}

	/**
	 * @param {Object} result
	 */
	onConsumedOffersReceived(result) {
		if (!result || !result.data || !result.data.dailyBonus || !result.data.dailyBonus.consumed) {
			return;
		}

		const whatIsConsumed = result.data.dailyBonus.consumed;
		this.setState({
			offers:     this.state.offers !== null ? this.state.offers.concat(whatIsConsumed.offers) : whatIsConsumed.offers,
			totalCount: whatIsConsumed.totalCount,
		});
	}

	/**
	 *
	 * @param {Object} result
	 * @param {Function} stateCallback
	 */
	onConsumedOffersReloaded(result, stateCallback = () => {
	}) {
		const hasConsumed    = result && result.data && result.data.dailyBonus && result.data.dailyBonus.consumed;
		const whatIsConsumed = hasConsumed ? result.data.dailyBonus.consumed : [];
		this.setState({
			offers:     whatIsConsumed.offers,
			totalCount: whatIsConsumed.totalCount,
		}, stateCallback);
	}

	onLoadMore() {
		Flux.Vxql.getConsumedOffers(this.onConsumedOffersReceived, 31, this.state.offers ? this.state.offers.length : 0);
	}

	onReloadOffers() {
		// Attempt to fix the issue when new offer isn't shown
		setTimeout(() => {
			Flux.Vxql.getConsumedOffers(this.onConsumedOffersReloaded, 31, 0, this.props.dateFrom, this.props.dateTo);
		}, 1000);
	}

	/**
	 * @param {Object} e
	 */
	onUrlClickHandler(e) {
		let uri = '';
		try {
			uri = new URL(e.target.href).pathname;
		} catch {
			uri = e.target.href;
		}
		ReloadHelper.reload(uri, e);
	}

	/**
	 * @param {Object} e
	 */
	onBecomeVIPButtonClicked(e) {
		if (!this.props.isMobile) {
			this.onUrlClickHandler(e);
		} else {
			VXPay.openAboPaytour();
		}
	}

	render() {
		if (!Flux.Guest.isLoggedIn()) {
			return (
				<div className="daily-bonus-page__collected-offers-items-no-offers-note">{Translations.get('DailyBonusControllerCollectedOffersLoginNeededNote')}</div>
			);
		}

		const offers = this.state.offers;
		if (!offers || offers.length === 0) {
			return (
				<div className="daily-bonus-page__collected-offers-items-no-offers-note">{Translations.get('DailyBonusControllerCollectedOffersNoDataNote')}</div>
			);

		}

		const iconMapping = {
			'video':         'videotake-full',
			'photo_album':   'image-album-lite',
			'MessengerGift': 'gift-line',
			'TopUpFlat':     'vxcoin',
			'TopUpPercent':  'vxcoin',
			'VipOneDay':     'person-vip',
			'Merchandise':   'merchandise-sale-lite',
			'VideoVoucher':  'voucher-icon',
		};

		let minDate = null;
		let maxDate = null;

		const offersList = offers.map((offer) => {
			const offerDate = new Date(Date.parse(offer.date));
			if (minDate === null || minDate > offerDate) {
				minDate = offerDate;
			}
			if (maxDate === null || maxDate < offerDate) {
				maxDate = offerDate;
			}

			const subClassName     = 'daily-bonus-page__collected-offers-items-list-container';
			const subClassNameMean = subClassName + (!this.props.isMobile ? '-red' : '-regular');
			let content            = null;
			let button             = null;
			let link               = null;
			let priceWithDiscount  = {
				value:    0,
				currency: 'EUR',
			};

			let buttonOnclick        = null;
			let buttonLink           = '';
			let buttonLinkIsExternal = false;
			let buttonColor          = '';
			let buttonText           = '';
			let buttonDisabled       = false;
			let buttonBorder         = STYLES.BORDER.NONE;

			switch (offer.offer) {
				case 'video':
					if (offer.content) {
						priceWithDiscount = offer.content.dailyBonusPrice !== null
							? offer.content.dailyBonusPrice
							: calculatePriceWithDiscount(offer.content.price, offer.effect);
					}
					content = (
						<span>
							<span className={subClassNameMean}>{formatEffectAsPlainText(offer.effect)} {Translations.get('DailyBonusControllerForVideo')} </span>
							{!this.props.isMobile && offer.content && <span>
								<span className={subClassName + '-regular'}>| {formatPrice(offer.content.price)} </span>
								<span className={subClassName + '-red'}>{formatPrice(priceWithDiscount)} </span>
								<span className={subClassName + '-regular'}>| {offer.content.title} | </span>
								{offer.content.model && offer.content.model.profileLink && (
									<a href={offer.content.model.profileLink}
									   onClick={this.onUrlClickHandler}
									   className={subClassName + '-link'}
									>{offer.content.model.name} »</a>
								)}
							</span>}
						</span>
					);

					buttonLink   = offer.content && offer.content.linkVX ? new URL(offer.content.linkVX).pathname : '';
					buttonColor  = COLOR_TYPE_TRANSPARENT;
					buttonBorder = STYLES.BORDER.OUTLINE;
					buttonText   = Translations.get('DailyBonusControllerGoToVideo');
					break;
				case 'photo_album':
					if (offer.content) {
						priceWithDiscount = offer.content.dailyBonusPrice !== null
							? offer.content.dailyBonusPrice
							: calculatePriceWithDiscount(offer.content.price, offer.effect);
					}
					content = (
						<span>
							<span className={subClassNameMean}>{formatEffectAsPlainText(offer.effect)} {Translations.get('DailyBonusControllerForGallery')} </span>
							{!this.props.isMobile && offer.content && <span>
								<span className={subClassName + '-regular'}>| {formatPrice(offer.content.price)} </span>
								<span className={subClassName + '-red'}>{formatPrice(priceWithDiscount)} </span>
								<span className={subClassName + '-regular'}>| {offer.content.title} | </span>
								{offer.content.model && offer.content.model.profileLink && (
									<a href={offer.content.model.profileLink}
									   onClick={this.onUrlClickHandler}
									   className={subClassName + '-link'}
									>{offer.content.model.name} »</a>
								)}
							</span>}
						</span>
					);

					buttonText   = !this.props.isMobile ? Translations.get('DailyBonusControllerGoToGallery') : Translations.get('DailyBonusControllerGoToGalleryShort');
					buttonLink   = offer.content && offer.content.linkVX ? new URL(offer.content.linkVX).pathname : '';
					buttonColor  = COLOR_TYPE_TRANSPARENT;
					buttonBorder = STYLES.BORDER.OUTLINE;
					break;
				case 'VideoVoucher':
					content = (
						<span>
							<span className={subClassNameMean}>{Translations.get('DailyBonusControllerVideoVoucher')} </span>
						</span>
					);

					buttonDisabled = true;
					buttonColor    = COLOR_TYPE_DISABLED;
					buttonText     = Translations.get('Secured');
					break;
				case 'MessengerGift':
					content = (
						<span>
							<span className={subClassNameMean}>{offer.quantity}{!this.props.isMobile ? ' x ' : ' '}{Translations.get('DailyBonusControllerMessengerGift')} </span>
							{!this.props.isMobile && offer.conditions.length > 0 && (
								<span className={subClassName + '-regular'}>
									{Translations.get('DailyBonusControllerFromTopUp', offer.conditions[0].value + getCurrencySymbol())}
								</span>
							)}
						</span>
					);

					buttonLink   = this.props.messengerLink;
					buttonColor  = COLOR_TYPE_TRANSPARENT;
					buttonBorder = STYLES.BORDER.OUTLINE;
					buttonText   = !this.props.isMobile ? Translations.get('DailyBonusControllerGiveAwayGifts') : Translations.get('DailyBonusControllerGiveAwayGiftsShort');
					break;
				case 'TopUpFlat':
				case 'TopUpPercent':
					content = (
						<span>
							<span className={subClassNameMean}>{formatEffectAsPlainText(offer.effect)} Bonus </span>
							{!this.props.isMobile &&
							<span className={subClassName + '-regular'}>{Translations.get('DailyBonusControllerWhenChargingReceived')}</span>}
						</span>
					);

					buttonDisabled = true;
					buttonColor    = COLOR_TYPE_TRANSPARENT;
					buttonBorder   = STYLES.BORDER.OUTLINE;
					buttonText     = Translations.get('Redeemed365');
					break;
				case 'VipOneDay':
					content = (
						<span>
							<span className={subClassName + '-regular'}>{Translations.get('DailyBonusControllerVipOneDayGrats')} </span>
							{!this.props.isMobile &&
							<span className={subClassName + '-red'}>{Translations.get('DailyBonusControllerVipOneDayGratsDescription')}</span>}
						</span>
					);

					buttonOnclick = this.onBecomeVIPButtonClicked;
					buttonColor   = COLOR_TYPE_TRANSPARENT;
					buttonBorder  = STYLES.BORDER.OUTLINE;
					buttonText    = !this.props.isMobile ? Translations.get('DailyBonusControllerGoToTheVipSubpage') : Translations.get('DailyBonusControllerGoToTheVipSubpageShort');
					break;
				case 'Merchandise':
					content = (
						<span>
							<span className={subClassNameMean}>{formatEffectAsPlainText(offer.effect)} {!this.props.isMobile ? Translations.get('DailyBonusControllerForSelectedMerchItems') : Translations.get('DailyBonusControllerForSelectedMerchItemsShort')} </span>
							{!this.props.isMobile &&
							<span className={subClassName + '-regular'}>{Translations.get('DailyBonusControllerForSelectedMerchItemsDescription')}</span>}
						</span>
					);

					buttonLink           = 'https://vstx.de/';
					buttonLinkIsExternal = true;
					buttonColor          = COLOR_TYPE_TRANSPARENT;
					buttonBorder         = STYLES.BORDER.OUTLINE;
					buttonText           = Translations.get('DailyBonusControllerMerchGoToShop');
					break;
				default:
					break;
			}

			if (buttonText && buttonColor) {

				let onClick = null;
				if (typeof buttonOnclick === "function") {
					onClick = buttonOnclick;
				} else if (buttonLink) {
					onClick = (e) => {
						if (buttonLinkIsExternal) {
							window.open(buttonLink, '_blank');
						} else {
							ReloadHelper.reload(buttonLink, e);
						}
					};
				}

				const style = {};

				if (this.props.isMobile) {
					link = <VXLink text={buttonText}
					               disabled={buttonDisabled}
					               onClick={onClick}
					               url={buttonLink ? buttonLink : '#'}
					               additionalClasses={'h-pt-8'}
					/>;
				} else {
					style.marginRight = "40px";
					button            = <VXButton line1={[new VXButtonLineConfig(buttonText)]}
					                              color={buttonColor}
					                              disabled={buttonDisabled}
					                              onClick={onClick}
					                              staticMode={true}
					                              overrideStyles={style}
					                              border={buttonBorder}
					/>;
				}
			}

			return <li>
				<span className={'daily-bonus-page__collected-offers-items-icon icon -icon-' + iconMapping[offer.offer]} />
				<span className="daily-bonus-page__collected-offers-items__content-container">
					<div className="daily-bonus-page__collected-offers-items-title">{Translations.get('DailyBonusControllerOfferFrom')} {formatDate(offerDate)}</div>
					{content}
					{link}
				</span>
				{button}
			</li>;

		});

		let loadMoreButton = null;
		if (this.props.showLoadMore && offers.length < this.state.totalCount) {
			loadMoreButton = (
				<button className="daily-bonus-page__collected-offers-items-footer-button btn -btn-color-transparent-bordered"
				        onClick={this.onLoadMore}
				>{Translations.get('DailyBonusControllerCollectedOffersLoadMore')}</button>
			);
		}

		return (
			<div>
				<p className="daily-bonus-page__collected-offers-items-time-range">{Translations.get('DailyBonusControllerFromDates')} {formatDate(minDate)} - {formatDate(maxDate)}</p>

				<ul className="daily-bonus-page__collected-offers-items-list-container">
					{offersList}
				</ul>
				<div className="daily-bonus-page__collected-offers-items-footer">
					{loadMoreButton}
					<div className="daily-bonus-page__collected-offers-items-footer-note">{Translations.get('DailyBonusControllerCollectedOffersFooterNote')}</div>
				</div>
			</div>
		);
	}
}

CollectedOffersList.defaultProps = {
	isMobile:     false,
	dateFrom:     null,
	dateTo:       null,
	showLoadMore: true,
};

CollectedOffersList.propTypes = {
	messengerLink: PropTypes.string.isRequired,             // /de/messenger/
	becomeVipLink: PropTypes.string,                        // /de/magazin/vipclub/
	isMobile:      PropTypes.bool,
	dateFrom:      PropTypes.string,
	dateTo:        PropTypes.string,
	showLoadMore:  PropTypes.bool,
};

export default CollectedOffersList;
